import { DEFAULT_LOCALE, type Enums, type Tables, type TablesInsert } from '@magicschool/supabase/types';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import { v4 as uuid } from 'uuid';
import {
  addAlertEmailAction,
  createGuardrailsInstanceAction,
  deleteConfigAction,
  getAdminOrgModerationConfigAction,
  getAlertEmailsAction,
  getGlobalKeywordListAction,
  getOrgInstitutionsAction,
  publishConfigAction,
  removeAlertEmailAction,
  saveModerationConfigAction,
  type TestModerationResult,
  testModerationAction,
  unpublishConfigAction,
  updateGuardrailsInstanceIdAction,
  updateModerationConfigAction,
} from '@/app/(teacher)/_actions/moderation/actions';
import { ALERT_EMAIL_ERROR_CODES, type AlertEmailErrorCode } from '@/app/(teacher)/_actions/moderation/errorCodes';
import type { ServerActionError } from '@/features/server/actions/types';
import { type ModerationCategoryName, ORDERED_MODERATION_CATEGORIES } from './moderationCategoryConstants';

/** Marks a locally-created row (denied topic, alert email) that hasn't been persisted yet. */
const UNSAVED_ID_PREFIX = 'unsaved-';

const hasErrorCode = (error: ServerActionError, code: AlertEmailErrorCode): boolean =>
  typeof error.errorInfo.cause === 'object' &&
  error.errorInfo.cause !== null &&
  (error.errorInfo.cause as { code?: unknown }).code === code;

/**
 * Type for config with joined relations from the database query.
 * Used when loading configs that include nested moderation_categories, keyword_lists, etc.
 */
type ModerationConfigWithRelations = Tables<'moderation_configs'> & {
  moderation_categories?: ModerationCategoryWithRequiredFields[];
  keyword_lists?: { moderation_keywords?: Partial<Tables<'moderation_keywords'>>[] } | null;
  keyword_list_id?: string | null;
  moderation_denied_topics?: Partial<Tables<'moderation_denied_topics'>>[];
};

/** Type for keyword data required by the save action */
type KeywordInput = { keyword: string; language: Enums<'language_enum'> };

export type ModerationCategoryWithRequiredFields = Partial<
  Omit<Tables<'moderation_categories'>, 'moderation_action' | 'moderation_category'>
> & {
  moderation_action: 'flag' | 'block' | 'end_thread';
  moderation_category: ModerationCategoryName;
  sensitivity: 'none' | 'low' | 'medium' | 'high';
};

export const DEFAULT_MODERATION_CATEGORIES: TablesInsert<'moderation_categories'>[] = ORDERED_MODERATION_CATEGORIES.map(
  (moderation_category) => ({
    moderation_category,
    alert_type: 'none',
    sensitivity: 'medium',
    moderation_action: 'flag',
  }),
);

export type ModerationSettingsStore = {
  loading: boolean;
  /** True once loadPublishedConfig has resolved at least once. */
  hasLoaded: boolean;
  dirty: boolean;
  tab: 'categories' | 'keywords' | 'alerting' | 'denied-topics';
  playgroundOpen: boolean;
  configId?: string;
  publishedConfigId?: string;
  configExists: boolean;
  guardrailsEnabled: boolean;
  customKeywordsEnabled: boolean;
  orgGuardrailsInstanceId: string | null;
  orgId: string | null;
  orgName: string;
  categories: ModerationCategoryWithRequiredFields[];
  keywords: Partial<Tables<'moderation_keywords'>>[];
  globalKeywords: Tables<'moderation_keywords'>[];
  savedConfigs: Tables<'moderation_configs'>[];
  alertEmails: { id: string; email: string; institutionId: string | null }[];
  publishedAlertEmails: { id: string; email: string; institutionId: string | null }[];
  institutions: { id: string; name: string }[];
  deniedTopics: Partial<Tables<'moderation_denied_topics'>>[];
  loadPublishedConfig: (orgId: string) => Promise<void>;
  loadSpecificConfig: (configId: string) => Promise<void>;
  createInitialConfig: () => Promise<void>;
  setField: SetField<ModerationSettingsStore>;
  saveVersion: (configName: string) => Promise<void>;
  saveAndPublish: (orgId: string, isEnabling?: boolean) => Promise<void>;
  publishVersion: (configId: string) => Promise<void>;
  unpublishConfig: (orgId: string) => Promise<void>;
  deleteVersion: (configId: string) => Promise<void>;
  updateCategory: (category: Partial<Tables<'moderation_categories'>>) => void;
  createKeywordList: () => void;
  deleteKeywordList: () => void;
  addKeyword: (keyword: string) => void;
  removeKeyword: (id: string) => void;
  resetKeywordsToDefault: () => void;
  testModeration: (input: string) => Promise<TestModerationResult>;
  addDeniedTopic: (
    topic: Omit<Tables<'moderation_denied_topics'>, 'id' | 'moderation_config_id' | 'created_at' | 'updated_at'>,
  ) => Promise<void>;
  updateDeniedTopic: (topic: Partial<Tables<'moderation_denied_topics'>> & { id: string }) => void;
  removeDeniedTopic: (id: string) => void;
  addScopedAlertEmail: (email: string, institutionId: string | null) => void;
  removeScopedAlertEmail: (emailId: string) => void;
  autoPauseEnabled: boolean;
  updateAutoPause: (enabled: boolean) => void;
};

const defaultState = {
  loading: false,
  hasLoaded: false,
  dirty: false,
  tab: 'categories' as const,
  playgroundOpen: false,
  configId: undefined,
  publishedConfigId: undefined,
  configExists: false,
  guardrailsEnabled: false,
  customKeywordsEnabled: false,
  orgGuardrailsInstanceId: null,
  orgId: null,
  orgName: '',
  categories: [],
  keywords: [],
  globalKeywords: [],
  savedConfigs: [],
  alertEmails: [],
  publishedAlertEmails: [],
  institutions: [],
  deniedTopics: [],
  autoPauseEnabled: true,
};

const mapAlertEmails = (rows: { id: string; email: string; institution_id: string | null }[] | null | undefined) =>
  (rows ?? []).map((row) => ({ id: row.id, email: row.email, institutionId: row.institution_id }));

export const createModerationSettingsStoreSlice = createStoreSlice(
  'ModerationSettingsStoreData',
  defaultState,
  ({ set, get, setField }) => {
    // Server actions never throw — they resolve with `{ data, error }` — so failures must be
    // surfaced explicitly or they resolve cleanly with no error shown.
    const throwIfActionFailed = (error: { message: string } | undefined) => {
      if (!error) return;
      set({ loading: false });
      throw new Error(error.message);
    };

    return {
      setField,
      loadPublishedConfig: async (orgId: string) => {
        set({ loading: true });
        const [{ data: orgConfigs }, { data: globalKeywordList }, { data: alertEmails }, { data: institutions }] = await Promise.all([
          getAdminOrgModerationConfigAction(orgId),
          getGlobalKeywordListAction(),
          getAlertEmailsAction(orgId),
          getOrgInstitutionsAction(orgId),
        ]);
        const mappedAlertEmails = mapAlertEmails(alertEmails);
        // Shared across all three branches so every one gets a correct publishedAlertEmails snapshot.
        const sharedLoadedFields = {
          globalKeywords: globalKeywordList ?? [],
          alertEmails: mappedAlertEmails,
          publishedAlertEmails: mappedAlertEmails,
          institutions: institutions ?? [],
        };

        if (orgConfigs && orgConfigs.configs.length > 0) {
          // Get the current config - prefer published, fall back to most recent
          const publishedConfig = orgConfigs.configs.find((config) => config.id === orgConfigs.settings?.published_config);
          const mostRecentConfig = orgConfigs.configs[0]; // configs are sorted by created_at desc
          const currentConfig = publishedConfig || mostRecentConfig;

          if (currentConfig) {
            set({
              dirty: false,
              configExists: true,
              orgGuardrailsInstanceId: orgConfigs.settings?.guardrails_instance_id,
              orgName: orgConfigs.orgName,
              orgId,
              configId: currentConfig.id,
              publishedConfigId: orgConfigs.settings?.published_config ?? undefined,
              guardrailsEnabled: currentConfig.guardrails_enabled ?? false,
              categories: currentConfig.moderation_categories as ModerationCategoryWithRequiredFields[],
              savedConfigs: orgConfigs.configs,
              keywords: currentConfig.keyword_lists?.moderation_keywords ?? [],
              customKeywordsEnabled: Boolean(currentConfig.keyword_list_id),
              deniedTopics: currentConfig.moderation_denied_topics ?? [],
              autoPauseEnabled: currentConfig.autopause_enabled ?? false,
              ...sharedLoadedFields,
            });
          } else {
            set({ ...defaultState, orgId, orgName: orgConfigs.orgName, ...sharedLoadedFields });
          }
        } else {
          set({ ...defaultState, orgId, orgName: orgConfigs?.orgName ?? '', ...sharedLoadedFields });
        }
        set({ loading: false, hasLoaded: true });
      },
      loadSpecificConfig: async (configId: string) => {
        const { savedConfigs } = get();
        set({ loading: true });
        const config = savedConfigs.find((c) => c.id === configId) as ModerationConfigWithRelations | undefined;

        if (config) {
          set({
            dirty: false,
            configId: config.id,
            guardrailsEnabled: config.guardrails_enabled ?? false,
            categories: config.moderation_categories ?? [],
            keywords: config.keyword_lists?.moderation_keywords ?? [],
            customKeywordsEnabled: Boolean(config.keyword_list_id),
            deniedTopics: config.moderation_denied_topics ?? [],
          });
        }

        set({ loading: false });
      },
      createInitialConfig: async () => {
        const { orgId, orgName, loadPublishedConfig, deniedTopics } = get();

        if (!orgId || !orgName) throw new Error('Org ID and org name are required for creating an initial config');

        set({ loading: true });
        const { error } = await saveModerationConfigAction(
          orgId,
          orgName,
          {
            name: 'Initial Version',
            guardrails_enabled: deniedTopics.length > 0,
            languages: [],
          },
          DEFAULT_MODERATION_CATEGORIES,
          [], // Don't clone keywords when creating initial config
          deniedTopics as TablesInsert<'moderation_denied_topics'>[],
          true,
        );
        throwIfActionFailed(error);
        await loadPublishedConfig(orgId ?? '');
        set({ loading: false });
      },
      saveVersion: async (configName?: string) => {
        set({ loading: true });
        const state = get();
        if (!state.orgId || !state.orgName) throw new Error('Org ID and org name are required for saving a version');

        // Filter keywords to only those with required fields
        const keywordsInput: KeywordInput[] = state.keywords
          .filter((k): k is Partial<Tables<'moderation_keywords'>> & { keyword: string } => Boolean(k.keyword))
          .map((k) => ({ keyword: k.keyword, language: k.language ?? DEFAULT_LOCALE }));

        // Filter denied topics to only those with a name (required field)
        const deniedTopicsToSave = state.deniedTopics.filter((t) => t.name && t.name.trim() !== '');

        const { data: savedConfigId } = await saveModerationConfigAction(
          state.orgId,
          state.orgName,
          {
            name: configName || 'Current Version',
            guardrails_enabled: deniedTopicsToSave.length > 0,
            languages: [],
          },
          state.categories,
          keywordsInput,
          deniedTopicsToSave as TablesInsert<'moderation_denied_topics'>[],
        );
        // Load configs again to get the new config
        await get().loadPublishedConfig(state.orgId ?? '');
        // Load the new config
        if (savedConfigId) await get().loadSpecificConfig(savedConfigId);

        set({ loading: false, dirty: false });
      },
      saveAndPublish: async (orgId: string, isEnabling = false) => {
        set({ loading: true });
        const state = get();
        if (!state.orgName) throw new Error('Org name is required for saving and publishing');

        // When enabling moderation (not just saving changes), don't copy keywords from previous configs
        const keywordsToSave = isEnabling ? [] : state.keywords;

        // Filter keywords to only those with required fields
        const keywordsInput: KeywordInput[] = keywordsToSave
          .filter((k): k is Partial<Tables<'moderation_keywords'>> & { keyword: string } => Boolean(k.keyword))
          .map((k) => ({ keyword: k.keyword, language: k.language ?? DEFAULT_LOCALE }));

        // Filter denied topics to only those with a name (required field)
        const deniedTopicsToSave = state.deniedTopics.filter((t) => t.name && t.name.trim() !== '');

        // If we have an existing published config, update it instead of creating a new one
        const { error: configError } =
          state.configId && state.publishedConfigId
            ? await updateModerationConfigAction(
                state.configId,
                orgId,
                state.orgName,
                {
                  guardrails_enabled: deniedTopicsToSave.length > 0,
                  autopause_enabled: state.autoPauseEnabled,
                },
                state.categories,
                keywordsInput,
                deniedTopicsToSave as TablesInsert<'moderation_denied_topics'>[],
              )
            : // Create new config and publish it
              await saveModerationConfigAction(
                orgId,
                state.orgName,
                {
                  name: 'Current Version',
                  guardrails_enabled: deniedTopicsToSave.length > 0,
                  languages: [],
                  autopause_enabled: state.autoPauseEnabled,
                },
                state.categories,
                keywordsInput,
                deniedTopicsToSave as TablesInsert<'moderation_denied_topics'>[],
                true, // shouldPublish = true
              );

        throwIfActionFailed(configError);

        const additions = state.alertEmails.filter((e) => e.id.startsWith(UNSAVED_ID_PREFIX));
        const removals = state.publishedAlertEmails.filter((published) => !state.alertEmails.some((e) => e.id === published.id));

        let emailSyncError: ServerActionError | undefined;
        // Additions/removals that never made it to the server on a failed sync — reapplied
        // onto the reloaded state below so a partial failure can't silently drop or undo a
        // buffered edit. A removal that never landed is still on the server and comes back
        // in the reload as if never removed, so it must be filtered back out explicitly —
        // unlike an addition, which the reload simply won't contain.
        let unsyncedAdditions: typeof additions = [];
        let unsyncedRemovals: typeof removals = [];

        // Removals before additions: removing X then re-adding X in the same scope would
        // otherwise race the insert against the delete on the DB's partial unique index.
        for (let i = 0; i < removals.length; i++) {
          const removed = removals[i];
          const { error } = await removeAlertEmailAction(removed.id);
          // Already gone server-side — treat a retry-after-partial-failure as success.
          if (error && !hasErrorCode(error, ALERT_EMAIL_ERROR_CODES.NOT_FOUND)) {
            emailSyncError = error;
            unsyncedRemovals = removals.slice(i);
            unsyncedAdditions = additions;
            break;
          }
        }

        if (!emailSyncError) {
          for (let i = 0; i < additions.length; i++) {
            const added = additions[i];
            const { error } = await addAlertEmailAction(orgId, added.email, added.institutionId);
            // Already landed server-side — same idempotency reasoning as above.
            if (error && !hasErrorCode(error, ALERT_EMAIL_ERROR_CODES.DUPLICATE)) {
              emailSyncError = error;
              unsyncedAdditions = additions.slice(i);
              break;
            }
          }
        }

        // Resync before rethrowing so a partial failure can't leave the snapshot stale.
        await get().loadPublishedConfig(orgId);

        if (emailSyncError) {
          // Re-apply whatever didn't make it to the server, and keep dirty so the layout's
          // Save button re-enables — the reload above cleared both as if nothing were pending.
          const reloadedAlertEmails = get().alertEmails.filter((row) => !unsyncedRemovals.some((removed) => removed.id === row.id));
          set({ dirty: true, alertEmails: [...reloadedAlertEmails, ...unsyncedAdditions] });
        }

        throwIfActionFailed(emailSyncError);
        set({ loading: false, dirty: false });
      },
      publishVersion: async (configId: string) => {
        set({ loading: true });
        const { orgId } = get();
        if (orgId) {
          const { error } = await publishConfigAction(orgId, configId);
          throwIfActionFailed(error);
        }
        set({ loading: false, publishedConfigId: configId });
      },
      unpublishConfig: async (orgId: string) => {
        set({ loading: true });
        await unpublishConfigAction(orgId);
        await get().loadPublishedConfig(orgId);
        set({ loading: false });
      },
      deleteVersion: async (configId: string) => {
        set({ loading: true });
        const { orgId, loadPublishedConfig } = get();
        await deleteConfigAction(orgId, configId);
        await loadPublishedConfig(orgId ?? '');
        set({ loading: false });
      },
      updateCategory: (category: Partial<Tables<'moderation_categories'>>) => {
        const categories = get().categories;

        if (!categories.find((c) => c.moderation_category === category.moderation_category)) {
          set({
            dirty: true,
            categories: [
              ...categories,
              { moderation_action: 'flag', alert_type: 'none', sensitivity: 'medium', ...category } as ModerationCategoryWithRequiredFields,
            ],
          });
        } else {
          const updatedCategories = categories.map((c) =>
            c.moderation_category === category.moderation_category ? ({ ...c, ...category } as ModerationCategoryWithRequiredFields) : c,
          );
          set({ dirty: true, categories: updatedCategories });
        }
      },
      createKeywordList: () => {
        set({ customKeywordsEnabled: true });
      },
      deleteKeywordList: () => {
        set({ customKeywordsEnabled: false });
      },
      addKeyword: (keyword: string) => {
        set({ dirty: true, keywords: [...get().keywords, { keyword: keyword, language: DEFAULT_LOCALE }] });
      },
      removeKeyword: (id: string) => {
        set({ dirty: true, keywords: get().keywords.filter((k) => k.id !== id) });
      },
      resetKeywordsToDefault: () => {
        const { globalKeywords } = get();
        set({ dirty: true, keywords: globalKeywords });
      },
      addDeniedTopic: async (topic) => {
        const { deniedTopics, orgGuardrailsInstanceId, orgId, orgName } = get();

        const newTopic = {
          ...topic,
          id: `${UNSAVED_ID_PREFIX}${uuid()}`,
          moderation_config_id: get().configId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // If this is the first denied topic and no guardrails instance exists, create one
        if (!orgGuardrailsInstanceId && deniedTopics.length === 0) {
          if (!orgId || !orgName) throw new Error('Org ID and org name are required for creating guardrails instance');

          setField('loading')(true);
          try {
            // Create guardrails instance with the first denied topic
            const { data: guardrailsInstanceId } = await createGuardrailsInstanceAction(orgId, orgName, [
              newTopic as TablesInsert<'moderation_denied_topics'>,
            ]);

            if (!guardrailsInstanceId) throw new Error('Failed to create guardrails instance');

            // Update the guardrails instance id in org_moderation_settings
            await updateGuardrailsInstanceIdAction(orgId, guardrailsInstanceId);

            // Update local state with the new guardrails instance ID and the new topic
            set({ orgGuardrailsInstanceId: guardrailsInstanceId, dirty: true, deniedTopics: [newTopic] });
          } finally {
            setField('loading')(false);
          }
        } else {
          // Just add the topic to the list
          set({ dirty: true, deniedTopics: [...deniedTopics, newTopic] });
        }
      },
      updateDeniedTopic: (topic) => {
        const topics = get().deniedTopics;
        const updatedTopics = topics.map((t) => (t.id === topic.id ? { ...t, ...topic, updated_at: new Date().toISOString() } : t));
        set({ dirty: true, deniedTopics: updatedTopics });
      },
      removeDeniedTopic: (id) => {
        set({ dirty: true, deniedTopics: get().deniedTopics.filter((t) => t.id !== id) });
      },
      addScopedAlertEmail: (email, institutionId) => {
        set({
          dirty: true,
          alertEmails: [...get().alertEmails, { id: `${UNSAVED_ID_PREFIX}${uuid()}`, email, institutionId }],
        });
      },
      removeScopedAlertEmail: (emailId) => {
        set({ dirty: true, alertEmails: get().alertEmails.filter((e) => e.id !== emailId) });
      },
      testModeration: async (input: string) => {
        const { guardrailsEnabled, categories, orgId, orgName, keywords, deniedTopics } = get();
        if (!orgId || !orgName) throw new Error('Org ID and org name are required for testing moderation');

        const { data: result } = await testModerationAction(
          orgId,
          orgName,
          input,
          {
            guardrails_enabled: guardrailsEnabled,
          },
          categories as Tables<'moderation_categories'>[],
          deniedTopics as Tables<'moderation_denied_topics'>[],
          keywords.map((k) => k.keyword).filter(Boolean) as string[],
        );

        if (!result) throw new Error('Failed to test moderation');

        return result;
      },
      updateAutoPause: (enabled: boolean) => {
        set({ dirty: true, autoPauseEnabled: enabled });
      },
    };
  },
);
